import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { tier1Sections, tier2Sections, tier3Sections } from '@/lib/preloadSections';
import {
  prefetchReferenceData,
  prefetchLeadsData,
  prefetchCustomersData,
  prefetchContractsData,
  prefetchAufgabenData,
  prefetchDashboardData,
} from '@/lib/prefetchData';
import { useNetworkStatus, getPrefetchTier } from '@/hooks/useNetworkStatus';
import { useIsMobile } from '@/hooks/use-mobile';

/**
 * Hook for intelligent section preloading
 * 
 * Preloads section bundles and data in the background based on priority tiers
 * and network connection quality:
 * - Reference data: Static data (Sparten, Gesellschaften, Bereiche) - preloaded immediately
 * - Tier 1: High-frequency sections (Leads, Customers, Contracts, Tasks) - bundles after 1s, data after 2s
 * - Tier 2: Medium-frequency sections (Partners, Downloads, etc.) - preloaded after 3s
 * - Tier 3: Low-frequency sections (Settings, Help, etc.) - preloaded after 5s
 * 
 * Connection-aware: Adjusts prefetching based on network quality to avoid
 * slowing down the app on slow connections (3G, 2G) or when data saver is enabled.
 * 
 * Uses requestIdleCallback when available for non-blocking preloading during browser idle time.
 */
export const usePreloadSections = (userId?: string) => {
  const hasPreloaded = useRef(false);
  const queryClient = useQueryClient();
  const networkStatus = useNetworkStatus();
  const isMobile = useIsMobile();

  useEffect(() => {
    // Only preload once per session and when user is authenticated
    if (hasPreloaded.current || !userId) return;
    hasPreloaded.current = true;

    const prefetchTier = getPrefetchTier(networkStatus);
    
    // No prefetch on slow connections or data saver mode
    if (prefetchTier === 0) {
      console.debug('[Prefetch] Disabled due to slow connection or data saver mode', {
        effectiveType: networkStatus.effectiveType,
        saveData: networkStatus.saveData,
        isOnline: networkStatus.isOnline
      });
      return;
    }

    /**
     * Preload sections with a delay, using idle time when possible
     */
    const preloadWithDelay = (
      sections: (() => Promise<any>)[],
      delay: number
    ) => {
      setTimeout(() => {
        if ('requestIdleCallback' in window) {
          // Use idle time to avoid blocking user interactions
          requestIdleCallback(
            () => {
              sections.forEach((loadFn) => {
                loadFn().catch((error) => {
                  // Silently fail - section will be loaded on-demand if preload fails
                  console.debug('Section preload failed:', error);
                });
              });
            },
            { timeout: 2000 } // Fallback timeout if browser stays busy
          );
        } else {
          // Fallback for browsers without requestIdleCallback
          sections.forEach((loadFn) => {
            loadFn().catch((error) => {
              console.debug('Section preload failed:', error);
            });
          });
        }
      }, delay);
    };

    // PROGRESSIVE LOADING STRATEGY - Prioritized & Mobile-Optimized
    
    // Priority 1: Dashboard data (immediate - most critical)
    prefetchDashboardData(queryClient, userId).catch((error) => {
      console.debug('Dashboard data prefetch failed:', error);
    });

    // Priority 2: Widget data after dashboard (500ms delay)
    setTimeout(() => {
      import('@/lib/prefetchData').then(({ prefetchEventsData, prefetchCalendarData }) => {
        prefetchEventsData(queryClient, userId).catch(() => {});
        prefetchCalendarData(queryClient, userId).catch(() => {});
      });
    }, 500);

    // Priority 3: Reference data (1s delay - shared across sections)
    setTimeout(() => {
      prefetchReferenceData(queryClient).catch((error) => {
        console.debug('Reference data prefetch failed:', error);
      });
    }, 1000);

    // Priority 4: Tier 1 section bundles (2s delay - delayed to avoid blocking)
    preloadWithDelay(tier1Sections, 2000);
    
    // Priority 5: Section data (3s delay) - DESKTOP ONLY for mobile optimization
    if (prefetchTier >= 2 && !isMobile) {
      setTimeout(() => {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => {
            prefetchLeadsData(queryClient, userId).catch(() => {});
            prefetchCustomersData(queryClient, userId).catch(() => {});
            prefetchContractsData(queryClient, userId).catch(() => {});
            prefetchAufgabenData(queryClient, userId).catch(() => {});
          }, { timeout: 2000 });
        } else {
          prefetchLeadsData(queryClient, userId).catch(() => {});
          prefetchCustomersData(queryClient, userId).catch(() => {});
          prefetchContractsData(queryClient, userId).catch(() => {});
          prefetchAufgabenData(queryClient, userId).catch(() => {});
        }
      }, 3000);
    }
    
    // Tier 2 & 3 - Only on good connections (3G+) and Desktop
    if (prefetchTier >= 2 && !isMobile) {
      preloadWithDelay(tier2Sections, 4000);
    }
    
    if (prefetchTier >= 3 && !isMobile) {
      preloadWithDelay(tier3Sections, 6000);
    }

    // Periodically refresh dashboard data in the background when tab is active
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        prefetchDashboardData(queryClient, userId).catch(() => {});
      }
    }, 5 * 60 * 1000);

    console.debug('[Prefetch] Section preloading initiated', {
      tier: prefetchTier,
      networkType: networkStatus.effectiveType,
      downlink: networkStatus.downlink
    });

    return () => {
      window.clearInterval(intervalId);
    };
  }, [userId, queryClient, networkStatus.effectiveType, networkStatus.saveData, networkStatus.isOnline]);
};
